import React, { ReactNode } from 'react'
import classNames from 'classnames'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { IFormattedDatetimeProps } from 'transfix-ui/components/FormattedDateTime'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { makeStyles, Box, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IFile, IBaseFileType, TFileType, TPodState } from 'apps/fleet-planner/types'
import { DocumentListItem } from './DocumentListItem'

/**
 * Used as an optional component when there are no results
 */
export const EmptyState = ({ children = 'No uploaded documents' }) => (
  <Box pt={2} pb={2} display='flex' alignItems='center'>
    <Typography variant='body2' color='textSecondary' component='div'>
      {children}
    </Typography>
  </Box>
)

const useDocumentListStyles = makeStyles(theme => ({
  root: {
    background: brandColors.white,
    border: `1px solid ${brandColors.coolGray4}`,
    boxShadow: '0 1px 2px rgb(0 0 0 / 15%)',
    borderRadius: theme.shape.borderRadius,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  itemWrapper: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${brandColors.coolGray1}`,
  },
  lastChild: {
    borderBottomColor: 'transparent',
  },
}))

interface IDocumentListProps<T extends IBaseFileType> {
  /**
   * @default false
   */
  isEditable?: boolean
  /**
   * @default false
   */
  isLoading?: boolean
  /**
   * @default false
   */
  isExternal?: boolean
  /**
   * podState is used to determine the icon and subtitle
   */
  podState?: TPodState
  /**
   * File type used in `onReplace`
   * @example 'pod'
   */
  fileType: TFileType
  /**
   * String used to render button text
   * @example 'Scale Ticket'
   */
  documentName: string
  /**
   * List of documents
   */
  documents: T[]
  /**
   * If a positive integer, limits the number of documents that can be uploaded.
   * When value is -1, no limit is applied
   * @default -1
   */
  limit?: number
  /**
   * React component rendered as the upload input
   * @example <UploadButtonInput />
   */
  UploadComponent?: ReactNode
  /**
   * React component rendered as the upload input
   * @example <InlineMessage type='alert' message='There was an error' />
   */
  ErrorComponent?: ReactNode
  /**
   * Displays when there are no results in `documents` array
   */
  EmptyStateComponent?: ReactNode
  /**
   * Pass optional props to <FomattedDateTime /> elements in <DocumentListItem />
   */
  FormattedDateTimeProps?: Partial<IFormattedDatetimeProps>
  /**
   * Used to remove a document and add another in the same action
   */
  onReplace?: (replacedUrl: IFile['url'], newFiles: IFile[], fileType: TFileType) => void
  /**
   * Allows for custom title based on document properties
   * @default undefined
   */
  getDocumentTitle?: (document: T) => string
  /**
   * Allows for custom author based on document properties
   * @default undefined
   */
  getDocumentSubtitle?: (document: T) => ReactNode
  /**
   * Message prop for InlineMessage component
   */
  alertMessage?: ReactNode
  rejectedDocuments?: T[]
}

/**
 * Shows a list of documents, which can be uploaded or replaced
 */
export function DocumentList<T extends IBaseFileType>({
  documents: documentsProp = [],
  limit = -1,
  UploadComponent,
  ErrorComponent,
  EmptyStateComponent,
  documentName,
  isEditable = false,
  onReplace: onReplaceProp,
  getDocumentTitle,
  getDocumentSubtitle,
  fileType,
  isLoading,
  FormattedDateTimeProps = {},
  alertMessage,
  podState,
  rejectedDocuments = [],
}: IDocumentListProps<T>) {
  const classes = useDocumentListStyles()

  /**
   * Generates a callback based on the document.id value in map loop
   */
  const createReplaceHandler = (document: T) => (documents: IFile[]) => {
    if (onReplaceProp && !!document?.url) {
      onReplaceProp(document.url, documents, fileType)
    }
  }

  const canUploadMoreDocuments = limit < 0 || documentsProp.length < limit

  /**
   * Use `limit` prop to only show a limited amount of docs
   *
   * This is relevant for PODs which should only display the latest upload
   * although the user could upload more
   */
  const sortedDocuments = orderBy(documentsProp, ({ createdAt }) => moment(createdAt).valueOf(), [
    'desc',
  ])

  const limitedDocuments = limit > 0 ? sortedDocuments.slice(0, limit) : sortedDocuments
  const movePodWarningToTopInCarrierAppEnabled = useFeatureFlag('movePodWarningToTopInCarrierApp')

  return (
    <Box className={classes.root}>
      {!movePodWarningToTopInCarrierAppEnabled && alertMessage && (
        <Box mt={2}>
          <InlineMessage message={alertMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
        </Box>
      )}
      {ErrorComponent && <Box pt={2}>{ErrorComponent}</Box>}
      {limitedDocuments.map(doc => {
        const index = limitedDocuments.indexOf(doc)
        const isLastChild = index === limitedDocuments.length - 1
        const onReplace = isEditable ? createReplaceHandler(doc) : undefined
        return (
          <Box
            key={index}
            className={classNames(classes.itemWrapper, {
              [classes.lastChild]: isLastChild,
            })}>
            <DocumentListItem
              isLoading={isLoading}
              onReplace={onReplace}
              document={doc}
              documentName={documentName}
              getDocumentTitle={getDocumentTitle}
              getDocumentSubtitle={getDocumentSubtitle}
              isLastChild={isLastChild && !canUploadMoreDocuments}
              podState={podState}
              FormattedDateTimeProps={FormattedDateTimeProps}
            />
          </Box>
        )
      })}
      {rejectedDocuments.length && !isLoading && !limitedDocuments.length ? (
        <Box mt={!canUploadMoreDocuments || alertMessage ? 2 : 0}>
          {rejectedDocuments.slice(0, 1).map(doc => {
            const index = rejectedDocuments.indexOf(doc)
            const isLastChild = index === rejectedDocuments.length - 1
            return (
              <Box key={index} mb={isLastChild ? 0 : 1}>
                <DocumentListItem
                  isLoading={isLoading}
                  documentName={doc.fileName || ''}
                  getDocumentTitle={() => `${doc.fileLabel}`}
                  getDocumentSubtitle={getDocumentSubtitle}
                  document={doc}
                  isLastChild={isLastChild}
                  podState={podState}
                  FormattedDateTimeProps={FormattedDateTimeProps}
                />
              </Box>
            )
          })}
        </Box>
      ) : null}
      {documentsProp.length < 1 && EmptyStateComponent}
      {canUploadMoreDocuments && UploadComponent}
    </Box>
  )
}
